/**
 * Density Validator
 *
 * Validates datacenter density constraints including power per rack,
 * weight limits, floor loading, rack space utilization, and cooling capacity.
 */

import type { ValidationMessage } from '../types/validation';
import { ValidationErrors, ValidationWarnings } from '../types/validation';

/**
 * Datacenter density limits by tier
 */
export interface DensityLimits {
  /** Maximum power per rack (kW) */
  maxPowerPerRackKw: number;
  /** Maximum weight per rack (lbs) */
  maxWeightPerRackLbs: number;
  /** Floor loading limit (lbs per sq ft) */
  floorLoadingLimitLbsPerSqFt: number;
  /** Maximum rack units (standard 42U) */
  maxRackUnits: number;
  /** Maximum cooling per rack (BTU/hr) */
  maxCoolingPerRackBtuPerHour: number;
  /** Warning threshold for power (% of max) */
  powerWarningThreshold: number;
  /** Warning threshold for cooling (% of max) */
  coolingWarningThreshold: number;
}

/**
 * Standard datacenter tiers
 */
export const DATACENTER_TIERS = {
  standard: {
    maxPowerPerRackKw: 15,
    maxWeightPerRackLbs: 1500,
    floorLoadingLimitLbsPerSqFt: 150,
    maxRackUnits: 42,
    maxCoolingPerRackBtuPerHour: 51180, // 15 kW × 3412
    powerWarningThreshold: 0.8,
    coolingWarningThreshold: 0.8,
  },
  'high-density': {
    maxPowerPerRackKw: 30,
    maxWeightPerRackLbs: 2000,
    floorLoadingLimitLbsPerSqFt: 250,
    maxRackUnits: 42,
    maxCoolingPerRackBtuPerHour: 102360, // 30 kW × 3412
    powerWarningThreshold: 0.85,
    coolingWarningThreshold: 0.85,
  },
  'liquid-cooled': {
    maxPowerPerRackKw: 100,
    maxWeightPerRackLbs: 3000,
    floorLoadingLimitLbsPerSqFt: 400,
    maxRackUnits: 42,
    maxCoolingPerRackBtuPerHour: 341200, // 100 kW × 3412
    powerWarningThreshold: 0.9,
    coolingWarningThreshold: 0.9,
  },
  'hyperscale-liquid': {
    maxPowerPerRackKw: 250,
    maxWeightPerRackLbs: 4000,
    floorLoadingLimitLbsPerSqFt: 500,
    maxRackUnits: 52, // Taller racks
    maxCoolingPerRackBtuPerHour: 853000, // 250 kW × 3412
    powerWarningThreshold: 0.9,
    coolingWarningThreshold: 0.9,
  },
} as const;

/**
 * Rack density metrics
 */
export interface RackDensityMetrics {
  /** Power per rack (kW) */
  powerPerRackKw: number;
  /** Weight per rack (lbs) */
  weightPerRackLbs: number;
  /** Rack units used */
  rackUnitsUsed: number;
  /** Cooling required (BTU/hr) */
  coolingRequiredBtuPerHour: number;
  /** Floor space per rack (sq ft) - typically 6-8 sq ft */
  floorSpacePerRackSqFt: number;
}

/**
 * Density violation
 */
export interface DensityViolation {
  /** Violation type */
  type: 'power' | 'weight' | 'floor-loading' | 'rack-space' | 'cooling';
  /** Severity */
  severity: 'error' | 'warning';
  /** Current value */
  currentValue: number;
  /** Limit value */
  limitValue: number;
  /** Unit of measure */
  unit: string;
  /** Utilization percentage */
  utilizationPercent: number;
  /** Description */
  message: string;
  /** Recommendation */
  recommendation: string;
}

/**
 * Density validation result
 */
export interface DensityValidationResult {
  /** Whether density is within limits */
  valid: boolean;
  /** Density metrics */
  metrics: RackDensityMetrics;
  /** Applied limits */
  limits: DensityLimits;
  /** Violations found */
  violations: DensityViolation[];
  /** Validation messages */
  messages: ValidationMessage[];
  /** Utilization summary */
  utilization: {
    powerUtilization: number;
    weightUtilization: number;
    spaceUtilization: number;
    coolingUtilization: number;
  };
}

/**
 * Calculates rack weight from components
 */
export function calculateRackWeight(
  nodeCount: number,
  weightPerNodeLbs: number,
  switchCount: number = 0,
  weightPerSwitchLbs: number = 0,
  rackChassisWeightLbs: number = 200 // Empty rack weight
): number {
  const nodeWeight = nodeCount * weightPerNodeLbs;
  const switchWeight = switchCount * weightPerSwitchLbs;
  const cableWeight = 50; // Estimate for cables and cable management
  const pduWeight = 40; // PDU weight

  return rackChassisWeightLbs + nodeWeight + switchWeight + cableWeight + pduWeight;
}

/**
 * Validates rack density against limits
 */
export function validateRackDensity(
  metrics: RackDensityMetrics,
  limits: DensityLimits
): DensityValidationResult {
  const violations: DensityViolation[] = [];
  const messages: ValidationMessage[] = [];

  // Validate power
  const powerUtilization = metrics.powerPerRackKw / limits.maxPowerPerRackKw;
  if (powerUtilization > 1.0) {
    violations.push({
      type: 'power',
      severity: 'error',
      currentValue: metrics.powerPerRackKw,
      limitValue: limits.maxPowerPerRackKw,
      unit: 'kW',
      utilizationPercent: powerUtilization * 100,
      message: `Rack power ${metrics.powerPerRackKw} kW exceeds limit of ${limits.maxPowerPerRackKw} kW`,
      recommendation: 'Reduce nodes per rack, use more efficient components, or upgrade to liquid cooling',
    });

    messages.push({
      severity: 'error',
      field: 'rack.powerPerRack',
      code: ValidationErrors.CONSTRAINT_VIOLATION,
      message: `Rack power exceeds datacenter limits`,
      details: `Current: ${metrics.powerPerRackKw} kW, Maximum: ${limits.maxPowerPerRackKw} kW`,
      suggestion: 'Consider reducing servers per rack or upgrading to a higher-density datacenter tier',
      currentValue: metrics.powerPerRackKw,
      expectedValue: limits.maxPowerPerRackKw,
    });
  } else if (powerUtilization > limits.powerWarningThreshold) {
    violations.push({
      type: 'power',
      severity: 'warning',
      currentValue: metrics.powerPerRackKw,
      limitValue: limits.maxPowerPerRackKw,
      unit: 'kW',
      utilizationPercent: powerUtilization * 100,
      message: `Rack power ${metrics.powerPerRackKw} kW is near limit of ${limits.maxPowerPerRackKw} kW`,
      recommendation: 'Consider planning for lower density or future capacity expansion',
    });

    messages.push({
      severity: 'warning',
      field: 'rack.powerPerRack',
      code: ValidationWarnings.POWER_CONCERN,
      message: `Rack power is ${(powerUtilization * 100).toFixed(0)}% of maximum`,
      suggestion: 'Leave headroom for future expansion or redundancy requirements',
      currentValue: metrics.powerPerRackKw,
      expectedValue: limits.maxPowerPerRackKw,
    });
  }

  // Validate weight
  const weightUtilization = metrics.weightPerRackLbs / limits.maxWeightPerRackLbs;
  if (weightUtilization > 1.0) {
    violations.push({
      type: 'weight',
      severity: 'error',
      currentValue: metrics.weightPerRackLbs,
      limitValue: limits.maxWeightPerRackLbs,
      unit: 'lbs',
      utilizationPercent: weightUtilization * 100,
      message: `Rack weight ${metrics.weightPerRackLbs} lbs exceeds limit of ${limits.maxWeightPerRackLbs} lbs`,
      recommendation: 'Reduce equipment per rack or use lighter-weight components',
    });

    messages.push({
      severity: 'error',
      field: 'rack.weight',
      code: ValidationErrors.CONSTRAINT_VIOLATION,
      message: `Rack weight exceeds safe limits`,
      details: `Current: ${metrics.weightPerRackLbs} lbs, Maximum: ${limits.maxWeightPerRackLbs} lbs`,
      suggestion: 'Reduce equipment density or reinforce floor loading capacity',
      currentValue: metrics.weightPerRackLbs,
      expectedValue: limits.maxWeightPerRackLbs,
    });
  }

  // Validate floor loading
  const floorLoading = metrics.weightPerRackLbs / metrics.floorSpacePerRackSqFt;
  const floorLoadingUtilization = floorLoading / limits.floorLoadingLimitLbsPerSqFt;
  if (floorLoadingUtilization > 1.0) {
    violations.push({
      type: 'floor-loading',
      severity: 'error',
      currentValue: floorLoading,
      limitValue: limits.floorLoadingLimitLbsPerSqFt,
      unit: 'lbs/sq ft',
      utilizationPercent: floorLoadingUtilization * 100,
      message: `Floor loading ${floorLoading.toFixed(0)} lbs/sq ft exceeds limit of ${limits.floorLoadingLimitLbsPerSqFt} lbs/sq ft`,
      recommendation: 'Increase floor space per rack or reinforce floor structure',
    });

    messages.push({
      severity: 'error',
      field: 'rack.floorLoading',
      code: ValidationErrors.PHYSICAL_IMPOSSIBILITY,
      message: `Floor loading exceeds structural limits`,
      details: `Current: ${floorLoading.toFixed(0)} lbs/sq ft, Maximum: ${limits.floorLoadingLimitLbsPerSqFt} lbs/sq ft`,
      suggestion: 'Consult structural engineer about floor reinforcement or spread load across more space',
      currentValue: floorLoading,
      expectedValue: limits.floorLoadingLimitLbsPerSqFt,
    });
  }

  // Validate rack space (RU)
  const spaceUtilization = metrics.rackUnitsUsed / limits.maxRackUnits;
  if (spaceUtilization > 1.0) {
    violations.push({
      type: 'rack-space',
      severity: 'error',
      currentValue: metrics.rackUnitsUsed,
      limitValue: limits.maxRackUnits,
      unit: 'RU',
      utilizationPercent: spaceUtilization * 100,
      message: `Rack units ${metrics.rackUnitsUsed}U exceeds available ${limits.maxRackUnits}U`,
      recommendation: 'Reduce equipment per rack or use shorter equipment',
    });

    messages.push({
      severity: 'error',
      field: 'rack.rackUnits',
      code: ValidationErrors.PHYSICAL_IMPOSSIBILITY,
      message: `Equipment does not fit in rack`,
      details: `Required: ${metrics.rackUnitsUsed}U, Available: ${limits.maxRackUnits}U`,
      suggestion: 'Reduce servers per rack or use taller rack enclosures',
      currentValue: metrics.rackUnitsUsed,
      expectedValue: limits.maxRackUnits,
    });
  } else if (spaceUtilization < 0.4) {
    messages.push({
      severity: 'info',
      field: 'rack.rackUnits',
      code: 'low_utilization',
      message: `Rack space is only ${(spaceUtilization * 100).toFixed(0)}% utilized`,
      suggestion: 'Consider increasing servers per rack to improve density',
    });
  }

  // Validate cooling
  const coolingUtilization = metrics.coolingRequiredBtuPerHour / limits.maxCoolingPerRackBtuPerHour;
  if (coolingUtilization > 1.0) {
    violations.push({
      type: 'cooling',
      severity: 'error',
      currentValue: metrics.coolingRequiredBtuPerHour,
      limitValue: limits.maxCoolingPerRackBtuPerHour,
      unit: 'BTU/hr',
      utilizationPercent: coolingUtilization * 100,
      message: `Cooling required ${metrics.coolingRequiredBtuPerHour.toLocaleString()} BTU/hr exceeds capacity of ${limits.maxCoolingPerRackBtuPerHour.toLocaleString()} BTU/hr`,
      recommendation: 'Reduce power density or upgrade cooling infrastructure',
    });

    messages.push({
      severity: 'error',
      field: 'rack.cooling',
      code: ValidationErrors.INSUFFICIENT_CAPACITY,
      message: `Cooling capacity insufficient for rack heat load`,
      details: `Required: ${metrics.coolingRequiredBtuPerHour.toLocaleString()} BTU/hr, Available: ${limits.maxCoolingPerRackBtuPerHour.toLocaleString()} BTU/hr`,
      suggestion: 'Upgrade to liquid cooling or reduce power density',
      currentValue: metrics.coolingRequiredBtuPerHour,
      expectedValue: limits.maxCoolingPerRackBtuPerHour,
    });
  } else if (coolingUtilization > limits.coolingWarningThreshold) {
    violations.push({
      type: 'cooling',
      severity: 'warning',
      currentValue: metrics.coolingRequiredBtuPerHour,
      limitValue: limits.maxCoolingPerRackBtuPerHour,
      unit: 'BTU/hr',
      utilizationPercent: coolingUtilization * 100,
      message: `Cooling utilization is ${(coolingUtilization * 100).toFixed(0)}% of capacity`,
      recommendation: 'Monitor cooling performance and plan for upgrades',
    });

    messages.push({
      severity: 'warning',
      field: 'rack.cooling',
      code: ValidationWarnings.THERMAL_CONCERN,
      message: `Cooling capacity is near maximum`,
      suggestion: 'Ensure adequate airflow and monitor inlet/outlet temperatures',
      currentValue: metrics.coolingRequiredBtuPerHour,
      expectedValue: limits.maxCoolingPerRackBtuPerHour,
    });
  }

  const valid = violations.filter((v) => v.severity === 'error').length === 0;

  return {
    valid,
    metrics,
    limits,
    violations,
    messages,
    utilization: {
      powerUtilization: Math.round(powerUtilization * 100) / 100,
      weightUtilization: Math.round(weightUtilization * 100) / 100,
      spaceUtilization: Math.round(spaceUtilization * 100) / 100,
      coolingUtilization: Math.round(coolingUtilization * 100) / 100,
    },
  };
}

/**
 * Recommends datacenter tier based on power density
 */
export function recommendDatacenterTier(powerPerRackKw: number): {
  recommendedTier: keyof typeof DATACENTER_TIERS;
  reason: string;
  limits: DensityLimits;
} {
  if (powerPerRackKw <= 15) {
    return {
      recommendedTier: 'standard',
      reason: 'Standard air-cooled datacenter suitable for ≤15 kW/rack',
      limits: DATACENTER_TIERS.standard,
    };
  } else if (powerPerRackKw <= 30) {
    return {
      recommendedTier: 'high-density',
      reason: 'High-density datacenter with enhanced cooling required for 15-30 kW/rack',
      limits: DATACENTER_TIERS['high-density'],
    };
  } else if (powerPerRackKw <= 100) {
    return {
      recommendedTier: 'liquid-cooled',
      reason: 'Liquid cooling required for 30-100 kW/rack density',
      limits: DATACENTER_TIERS['liquid-cooled'],
    };
  } else {
    return {
      recommendedTier: 'hyperscale-liquid',
      reason: 'Advanced liquid/immersion cooling required for >100 kW/rack density',
      limits: DATACENTER_TIERS['hyperscale-liquid'],
    };
  }
}
